import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  NotificationsArrowRightIcon,
  ReadIcon,
} from '~/shared/components/Icons/Icons';
import {
  NotificationTypes,
  type NotificationIconType,
  type NotificationUrgency,
} from './types';
import { URGENCY_CONFIG } from './utils/severietyMapping';
import { NotificationUrgencyMessageBox } from './NotificationUrgencyMessageBox';
import { notificationStyles } from '~/shared/theme/theme';
import { NotificationCardHeader } from './NotificationCardHeader';

interface NotificationCardProps {
  urgency: NotificationUrgency;
  title: string;
  deviceTitle: string;
  alertDescription: string;
  date: string;
  resolvedDate?: string;
  isRead: boolean;
  isResolved: boolean;
  type: NotificationIconType;
  typeLabel: string;
  onMarkAsRead?: () => void;
  onSeeDetails?: () => void;
}

export function NotificationCard({
  urgency,
  title,
  deviceTitle,
  alertDescription,
  date,
  resolvedDate,
  isRead,
  isResolved,
  type,
  typeLabel,
  onMarkAsRead,
  onSeeDetails,
}: NotificationCardProps) {
  const { t } = useTranslation();
  const config = URGENCY_CONFIG[urgency];

  return (
    <Box
      sx={{
        p: 4,
        pt: 5,
        borderRadius: 4,
        backgroundColor: 'background.paper',
        border: '1px solid #E5E5E5',
        mb: 2,
        mx: 6,
      }}
    >
      <NotificationCardHeader
        type={type}
        alertDescription={alertDescription}
        title={title}
        typeLabel={typeLabel}
        date={date}
        resolvedDate={resolvedDate}
        isResolved={isResolved}
      />
      <Box
        sx={{
          backgroundColor: 'background.default',
          padding: '4px 8px',
          width: 'fit-content',
          borderRadius: '4px',
        }}
      >
        <Typography
          variant="body2"
          color={notificationStyles.lightText}
          fontSize="12px"
          lineHeight="16px"
        >
          {deviceTitle}
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 4,
          mb: 4,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Stack direction="row" justifyContent="space-between">
        {onMarkAsRead && (
          <Button
            variant="text"
            size="small"
            onClick={onMarkAsRead}
            disabled={isRead}
            className="filterButton"
            disableRipple
          >
            {isRead ? (
              <>
                <ReadIcon sx={{ height: 14, pr: '5px' }} />
                <Typography fontSize={14} sx={{ opacity: 0.6 }}>
                  {t('notifications.read')}
                </Typography>
              </>
            ) : (
              <Typography
                fontWeight={600}
                fontSize={14}
                sx={{ '&:hover': { color: notificationStyles.hoverColor } }}
              >
                {t('notifications.markAsRead')}
              </Typography>
            )}
          </Button>
        )}
        {onSeeDetails && (
          <Button
            variant="text"
            size="small"
            onClick={onSeeDetails}
            className="filterButton"
            disableRipple
          >
            <Typography
              fontSize={14}
              fontWeight={600}
              sx={{
                '&:hover': { color: notificationStyles.hoverColor },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {t('notifications.seeDetails')}
              <NotificationsArrowRightIcon
                sx={{
                  fontSize: 'inherit',
                  ml: '5px',
                }}
              />
            </Typography>
          </Button>
        )}
      </Stack>

      {config.urgencyMessageKey &&
        !isResolved &&
        type !== NotificationTypes.SOLVED && (
          <NotificationUrgencyMessageBox urgency={urgency} />
        )}
    </Box>
  );
}
